import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Target, 
  Flame,
  ThermometerSun,
  Snowflake,
  Phone,
  Mail,
  Calendar,
  User,
  Building2,
  MessageSquare,
  Filter,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Leads() {
  const [searchParams] = useSearchParams();
  const { getAuthHeader } = useAuth();
  
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [statusFilter]);

  const fetchLeads = async () => {
    try {
      let url = `${API}/leads?limit=100`;
      if (statusFilter && statusFilter !== "all") url += `&status=${statusFilter}`;
      if (searchParams.get("campaign")) url += `&campaign_id=${searchParams.get("campaign")}`;
      
      const res = await axios.get(url, { headers: getAuthHeader() });
      setLeads(res.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const updateLead = async (leadId, data) => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v !== undefined && v !== null) formData.append(k, v);
      });
      
      await axios.put(`${API}/leads/${leadId}`, formData, { headers: getAuthHeader() });
      toast.success("Lead updated");
      fetchLeads();
      setSelectedLead(null);
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "hot": return <Flame className="w-4 h-4 text-red-500" />;
      case "warm": return <ThermometerSun className="w-4 h-4 text-amber-500" />;
      case "cold": return <Snowflake className="w-4 h-4 text-blue-500" />;
      case "qualified": return <Target className="w-4 h-4 text-emerald-500" />;
      case "converted": return <Target className="w-4 h-4 text-purple-500" />;
      default: return <Target className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "hot": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "warm": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "cold": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "qualified": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "converted": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const exportLeads = () => {
    const csv = [
      ["Name", "Phone", "Email", "Company", "Status", "Score", "Notes", "Date"].join(","),
      ...leads.map(lead => [
        `"${lead.contact?.first_name || ""} ${lead.contact?.last_name || ""}"`,
        lead.contact?.phone || "",
        lead.contact?.email || "",
        `"${lead.contact?.company || ""}"`,
        lead.status,
        lead.score,
        `"${lead.notes || ""}"`,
        lead.created_at
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce"></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="leads-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Leads</h1>
          <p className="text-muted-foreground">Manage and follow up with qualified leads</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="hot">🔥 Hot</SelectItem>
              <SelectItem value="warm">☀️ Warm</SelectItem>
              <SelectItem value="cold">❄️ Cold</SelectItem>
              <SelectItem value="qualified">✅ Qualified</SelectItem>
              <SelectItem value="converted">💜 Converted</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportLeads} data-testid="export-leads-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: "Total", count: leads.length, color: "bg-gray-500" },
          { label: "Hot", count: leads.filter(l => l.status === "hot").length, color: "bg-red-500" },
          { label: "Warm", count: leads.filter(l => l.status === "warm").length, color: "bg-amber-500" },
          { label: "Cold", count: leads.filter(l => l.status === "cold").length, color: "bg-blue-500" },
          { label: "Converted", count: leads.filter(l => l.status === "converted").length, color: "bg-purple-500" },
        ].map((stat) => (
          <Card key={stat.label} className="bg-card border-border">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-muted-foreground">{stat.label}</span>
              <span className={`text-2xl font-bold`}>{stat.count}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Leads Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leads.length === 0 ? (
          <Card className="col-span-full bg-card border-border border-dashed">
            <CardContent className="py-12 text-center">
              <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No leads yet</h3>
              <p className="text-muted-foreground">
                Run campaigns or take calls to generate qualified leads
              </p>
            </CardContent>
          </Card>
        ) : (
          leads.map((lead) => (
            <Card 
              key={lead.id} 
              className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => setSelectedLead(lead)}
              data-testid={`lead-${lead.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(lead.status)}
                    <Badge className={getStatusColor(lead.status)}>
                      {lead.status.toUpperCase()}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Score: {lead.score}/100
                  </span>
                </div>
                
                <h3 className="font-semibold mb-2">
                  {lead.contact?.first_name} {lead.contact?.last_name}
                </h3>
                
                <div className="space-y-1 text-sm text-muted-foreground">
                  {lead.contact?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3" />
                      {lead.contact.phone}
                    </div>
                  )}
                  {lead.contact?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {lead.contact.email}
                    </div>
                  )}
                  {lead.contact?.company && (
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3 h-3" />
                      {lead.contact.company}
                    </div>
                  )}
                </div>

                {lead.ai_summary && (
                  <p className="text-xs text-muted-foreground mt-3 line-clamp-2 italic">
                    "{lead.ai_summary}"
                  </p>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(lead.created_at)}
                  </span>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); }}>
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Notes
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedLead?.contact?.first_name} {selectedLead?.contact?.last_name}
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedLead.contact?.phone}</span>
                </div>
                {selectedLead.contact?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedLead.contact.email}</span>
                  </div>
                )}
                {selectedLead.contact?.company && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedLead.contact.company}</span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="text-sm font-medium mb-2 block">Status</label>
                <div className="flex gap-2 flex-wrap">
                  {["hot", "warm", "cold", "qualified", "converted", "lost"].map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedLead.status === status ? "default" : "outline"}
                      className={selectedLead.status === status ? getStatusColor(status) : ""}
                      onClick={() => updateLead(selectedLead.id, { status })}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* AI Summary */}
              {selectedLead.ai_summary && (
                <div>
                  <label className="text-sm font-medium mb-2 block">AI Summary</label>
                  <p className="text-sm text-muted-foreground p-3 rounded-lg bg-secondary/50 italic">
                    "{selectedLead.ai_summary}"
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Textarea
                  placeholder="Add notes about this lead..."
                  defaultValue={selectedLead.notes}
                  onBlur={(e) => {
                    if (e.target.value !== selectedLead.notes) {
                      updateLead(selectedLead.id, { notes: e.target.value });
                    }
                  }}
                />
              </div>

              {/* Follow-up */}
              <div>
                <label className="text-sm font-medium mb-2 block">Follow-up Date</label>
                <input
                  type="date"
                  className="w-full p-2 rounded-md border border-border bg-background"
                  defaultValue={selectedLead.follow_up_date?.split("T")[0]}
                  onChange={(e) => updateLead(selectedLead.id, { follow_up_date: e.target.value })}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button className="flex-1" onClick={() => window.open(`tel:${selectedLead.contact?.phone}`)}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
                {selectedLead.contact?.email && (
                  <Button variant="outline" className="flex-1" onClick={() => window.open(`mailto:${selectedLead.contact.email}`)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
